"use client";

import { MemberAccess, ProjectMemberRole } from "@prisma/client";
import { useQueryClient } from "@tanstack/react-query";
import Button, { ButtonType } from "@/app/(components)/generics/Button/Button";
import IconEdit from "@/app/(components)/generics/Icons/IconEdit";
import IconPlus from "@/app/(components)/generics/Icons/IconPlus";
import IconTrash from "@/app/(components)/generics/Icons/IconTrash";
import PointsLoader from "@/app/(components)/generics/loaders/PointsLoader/PointsLoader";
import {
  useBrand,
  useCanManageCompany,
} from "@/app/(components)/specifics/wrappers/BrandProvider/BrandProvider";
import { cancelProjectInvitation } from "@/app/actions/project-member/action";
import { useFetchProjectInvitations } from "@/app/hooks/fetchs/project-member/useFetchProjectInvitations";
import { ModalStore } from "@/app/stores/ModalStore";
import { TKey } from "@/app/translations/TKey";
import { useT } from "@/app/translations/useT";
import { PageParams } from "@/app/types/common/DefaultProps";
import { ProjectMemberData } from "@/app/types/project/ProjectMember";
import { Limits } from "@/app/vars/Limits";
import { QueryKey } from "@/app/vars/QueryKeys";
import { ROLE_LABEL_KEY } from "./Members.logic";
import styles from "./Members.module.css";

type Props = {
  params: PageParams;
  projectId: number;
  members: ProjectMemberData[];
  isLoading?: boolean;
};

export default function Members(props: Props) {
  const T = useT();
  const { params, projectId, members, isLoading = false } = props;

  const isTenant = useBrand() !== null;
  const canManageCompany = useCanManageCompany();
  const canManage = isTenant && canManageCompany;
  const externalMembers = members.filter((member) => !member.isCompanyMember);
  const atLimit = externalMembers.length >= Limits.MAX_PROJECT_MEMBERS;

  const queryClient = useQueryClient();
  const { data: invitations = [] } = useFetchProjectInvitations({ projectId, enabled: canManage });

  const handleCancelInvitation = async (invitationId: number) => {
    await cancelProjectInvitation(invitationId);
    queryClient.invalidateQueries({ queryKey: [QueryKey.PROJECT_INVITATIONS, projectId] });
  };

  const handleAdd = () => {
    ModalStore.getState().openProjectMemberForm(params, projectId);
  };

  const handleEdit = (member: ProjectMemberData) => {
    ModalStore.getState().openProjectMemberForm(params, projectId, { member });
  };

  const handleDelete = (member: ProjectMemberData) => {
    ModalStore.getState().openDeleteProjectMember(params, projectId, member);
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <span className={styles.count}>
          {externalMembers.length} / {Limits.MAX_PROJECT_MEMBERS}
        </span>
        {canManage && !atLimit && (
          <Button
            min={true}
            isLink={false}
            type={ButtonType.OUTLINE}
            text={T(TKey.PROJECT_MEMBER_ADD) as string}
            beforeIcon={<IconPlus size={14} />}
            onClick={handleAdd}
          />
        )}
      </div>

      {isLoading ? (
        <div className={styles.loaderWrapper}>
          <PointsLoader size={6} gap={7} color="var(--color-green-3)" delay={300} />
        </div>
      ) : externalMembers.length === 0 ? (
        <p className={styles.empty}>{T(TKey.PROJECT_MEMBERS_EMPTY)}</p>
      ) : (
        <ul className={styles.list}>
          {externalMembers.map((member) => (
            <li key={member.id} className={styles.row}>
              <div className={styles.rowInfo}>
                <span className={styles.rowName}>{member.name}</span>
                {member.user?.email && <span className={styles.rowEmail}>{member.user.email}</span>}
                {member.access && (
                  <span className={styles.accessBadge}>
                    {T(
                      member.access === MemberAccess.EDITOR
                        ? TKey.PROJECT_MEMBER_ACCESS_EDITOR
                        : TKey.PROJECT_MEMBER_ACCESS_READER,
                    )}
                  </span>
                )}
              </div>
              <div className={styles.rowEnd}>
                {!isTenant && (
                  <span className={styles.roleBadge}>{T(ROLE_LABEL_KEY[member.role])}</span>
                )}
                {canManage && (
                  <div className={styles.rowActions}>
                    <span className={styles.editAction} onClick={() => handleEdit(member)}>
                      <IconEdit width={16} height={16} />
                    </span>
                    {member.role !== ProjectMemberRole.OWNER && (
                      <span className={styles.deleteAction} onClick={() => handleDelete(member)}>
                        <IconTrash size={16} />
                      </span>
                    )}
                  </div>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {canManage && invitations.length > 0 && (
        <>
          <p className={styles.sectionTitle}>{T(TKey.INVITATIONS_PENDING_TITLE)}</p>
          <ul className={styles.list}>
            {invitations.map((invitation) => (
              <li key={invitation.id} className={styles.row}>
                <div className={styles.rowInfo}>
                  <span className={styles.rowName}>{invitation.email}</span>
                </div>
                <div className={styles.rowEnd}>
                  <span className={styles.pendingBadge}>{T(TKey.INVITATION_PENDING_BADGE)}</span>
                  <div className={styles.inviteActions}>
                    <span
                      className={styles.deleteAction}
                      onClick={() => handleCancelInvitation(invitation.id)}
                    >
                      <IconTrash size={16} />
                    </span>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
